import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from 'chart.js';
import type { ChartDataset } from 'chart.js';
import { brandFontStack, headingFontStack, bodyFontStack, getDefaultTheme } from './theme';
import { INFO_CARDS } from './infoCards';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
);

interface PriceSeries {
  region: number;
  date: string;
  dailyPriceArray: number[];
  dailyPriceArrayWithSupport: number[];
  dailyPriceAverage: number;
  dailyPriceMax: number;
  dailyPriceMin: number;
  dailyPriceAverageWithSupport: number;
  dailyPriceMaxWithSupport: number;
  dailyPriceMinWithSupport: number;
}

export interface WidgetOptions {
  apiBaseUrl: string;
  apiKey?: string;
  region: number;
  container: HTMLElement;
}

interface WidgetState {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  chart?: Chart;
  carouselInterval?: number;
  currentCardIndex?: number;
  useSupport: boolean;
  priceMode: 'with' | 'without' | 'norgespris';
  currentPriceData?: PriceSeries;
  cardDisplayHeight?: number;
}

const STYLE_ELEMENT_ID = 'strompris-widget-styles';

function applyVAT(priceOre: number): number {
  return priceOre * 1.25;
}

function ensureStylesInjected(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }

  // Load Google Fonts
  if (!document.getElementById('strompris-widget-fonts')) {
    const fontLink = document.createElement('link');
    fontLink.id = 'strompris-widget-fonts';
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&family=Montserrat:wght@400;500;600;700&display=swap';
    document.head.appendChild(fontLink);
  }

  const theme = getDefaultTheme();
  const styles = document.createElement('style');
  styles.id = STYLE_ELEMENT_ID;
  styles.textContent = `
    .strompris-widget {
      font-family: ${bodyFontStack};
      background: #ffffff;
      color: ${theme.primaryText};
      border-radius: 9px;
      padding: 10px 18px 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      max-width: 420px;
      border: 1px solid #e5e7eb;
    }

    @media (min-width: 1024px) {
      .strompris-widget {
        max-width: none;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
      }
    }

    .strompris-widget__header {
      display: flex;
      justify-content: flex-start;
      align-items: baseline;
      margin-bottom: 2px;
      padding: 0;
      border-bottom: none;
      background: transparent;
    }

    @media (min-width: 1024px) {
      .strompris-widget__header {
        grid-column: 1 / -1;
      }
    }

    .strompris-widget__title {
      font-family: ${headingFontStack};
      font-size: 24px;
      font-weight: 700;
      margin: 0;
      color: ${theme.primaryText};
      letter-spacing: -0.3px;
    }

    .strompris-widget__left {
      display: contents;
    }

    @media (min-width: 1024px) {
      .strompris-widget__left {
        display: flex;
        flex-direction: column;
        gap: 9px;
      }
    }

    .strompris-widget__right {
      display: none;
    }

    @media (min-width: 1024px) {
      .strompris-widget__right {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-top: -8px;
      }
    }

    .strompris-widget__hours-title {
      font-size: 18px;
      font-weight: 700;
      margin: 0 0 12px 0;
      color: ${theme.primaryText};
    }

    .strompris-widget__hours-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      max-height: 400px;
      overflow-y: auto;
    }

    .strompris-widget__hour-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      background: #f8f9fa;
      border-radius: 8px;
      font-size: 14px;
    }

    .strompris-widget__hour-item--current {
      background: #e3f2fd;
      border: 1px solid #3577c0;
    }

    .strompris-widget__hour-item--cheapest {
      background: #e8f5e9;
      border: 1px solid #059669;
    }

    .strompris-widget__hour-item--most-expensive {
      background: #ffebee;
      border: 1px solid #dc2626;
    }

    .strompris-widget__hour-time {
      font-weight: 600;
      color: #1f2933;
    }

    .strompris-widget__hour-price {
      font-weight: 700;
      color: #475569;
    }

    .strompris-widget__info-cards {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .strompris-widget__card-display {
      background: #f8f9fa;
      border-radius: 9px;
      padding: 16px;
      min-height: 206px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .strompris-widget__card {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 18px;
      align-items: center;
      width: 100%;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card {
        grid-template-columns: 1fr;
        gap: 14px;
        text-align: center;
      }
    }

    .strompris-widget__card-image {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .strompris-widget__card-icon {
      width: 160px;
      height: 160px;
      object-fit: contain;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-icon {
        width: 140px;
        height: 140px;
      }
    }

    .strompris-widget__card-content {
      text-align: left;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-content {
        text-align: center;
      }
    }

    .strompris-widget__card-headline {
      font-family: ${headingFontStack};
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 6px 0;
      color: #1f2933;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-headline {
        font-size: 18px;
      }
    }

    .strompris-widget__card-title {
      font-family: ${headingFontStack};
      font-size: 18px;
      font-weight: 400;
      margin: 6px 0 0 0;
      color: #1f2933;
      line-height: 1.4;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-title {
        font-size: 16px;
      }
    }

    .strompris-widget__card-price {
      display: flex;
      align-items: baseline;
      gap: 4px;
      margin: 0;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-price {
        justify-content: center;
      }
    }

    .strompris-widget__card-value {
      font-size: 40px;
      font-weight: 700;
      color: #3577c0;
      line-height: 1;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-value {
        font-size: 42px;
      }
    }

    .strompris-widget__card-unit {
      font-size: 20px;
      font-weight: 600;
      color: #475569;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-unit {
        font-size: 20px;
      }
    }

    .strompris-widget__card-description {
      font-size: 13px;
      color: #52616b;
      margin: 4px 0 0 0;
      line-height: 1.5;
    }

    .strompris-widget__card-dropdown-container {
      margin-top: 8px;
      display: flex;
      justify-content: flex-start;
    }

    @media (min-width: 1024px) {
      .strompris-widget__card-dropdown-container {
        justify-content: center;
      }
    }

    .strompris-widget__support-dropdown {
      font-family: ${bodyFontStack};
      font-size: 14px;
      padding: 6px 14px;
      border: 1px solid #cbd5e0;
      border-radius: 0;
      background: white;
      color: #1f2933;
      cursor: pointer;
      outline: none;
      transition: all 0.2s ease;
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 8px center;
      background-size: 16px;
      padding-right: 28px;
    }

    .strompris-widget__support-dropdown:hover {
      border-color: #3577c0;
      box-shadow: 0 2px 8px rgba(53, 119, 192, 0.15);
    }

    .strompris-widget__support-dropdown:focus {
      border-color: #3577c0;
      box-shadow: 0 0 0 3px rgba(53, 119, 192, 0.1);
    }

    .strompris-widget__card-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
    }

    .strompris-widget__card-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #cbd5e0;
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .strompris-widget__card-dot:hover {
      background: #94a3b8;
      transform: scale(1.1);
    }

    .strompris-widget__card-dot.active {
      background: #94a3b8;
      width: 16px;
      border-radius: 5px;
    }

    .strompris-widget__stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0;
      margin-bottom: 6px;
      border-bottom: 1px solid #e2e8f0;
    }

    .strompris-widget__stat {
      background: transparent;
      border-radius: 0;
      padding: 0 0 12px 0;
      border: none;
      text-align: center;
      position: relative;
    }

    .strompris-widget__stat:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 60%;
      background: #e2e8f0;
    }

    .strompris-widget__label {
      font-size: 11px;
      color: #94a3b8;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 400;
    }

    .strompris-widget__value {
      font-size: 28px;
      font-weight: 700;
      color: #1e293b;
      line-height: 1;
    }

    .strompris-widget__value--current {
      color: #0f172a;
    }

    .strompris-widget__value--avg {
      color: #475569;
    }

    .strompris-widget__value--max {
      color: #dc2626;
    }

    .strompris-widget__value--min {
      color: #059669;
    }
    
    .strompris-widget__unit {
      font-size: 11px;
      color: #94a3b8;
      font-weight: 400;
      margin-top: 2px;
    }

    .strompris-widget__chart {
      position: relative;
      width: 100%;
      height: 199px;
      margin-bottom: 4px;
      background: transparent;
      border-radius: 0;
      padding: 0;
    }

    .strompris-widget__chart-legend {
      display: flex;
      gap: 12px;
      padding: 0 0 6px 0;
    }

    .strompris-widget__legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .strompris-widget__legend-color {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 0;
    }

    .strompris-widget__legend-label {
      font-size: 13px;
      color: #52616b;
      font-family: ${bodyFontStack};
    }

    .strompris-widget__footer {
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: center;
      font-size: 13px;
      color: ${theme.secondaryText};
      padding-top: 0;
      padding: 0;
      border-top: none;
      background: transparent;
      white-space: nowrap;
    }

    .strompris-widget__source {
      color: ${theme.accent};
      text-decoration: none;
      font-weight: 400;
      transition: opacity 0.2s ease;
    }

    .strompris-widget__source:hover {
      opacity: 0.8;
      text-decoration: underline;
    }

    .strompris-widget__error {
      padding: 16px;
      border-radius: 14px;
      background: #fee2e2;
      color: #b91c1c;
      font-size: 14px;
      text-align: center;
      border: 1px solid #fecaca;
    }

    @media (max-width: 480px) {
      .strompris-widget {
        padding: 20px;
        border-radius: 16px;
      }

      .strompris-widget__title {
        font-size: 20px;
      }

      .strompris-widget__stats {
        gap: 10px;
      }

      .strompris-widget__stat {
        padding: 14px 12px;
      }

      .strompris-widget__value {
        font-size: 20px;
      }

      .strompris-widget__chart {
        height: 180px;
      }
    }
  `;

  document.head.appendChild(styles);
}

async function fetchPrices(options: WidgetOptions): Promise<PriceSeries | null> {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;

  const base = options.apiBaseUrl.replace(/\/+$/, '');
  const url = new URL(`prices/${dateString}`, `${base}/`);
  url.searchParams.set('region', String(options.region));

  const init: RequestInit = {};
  if (options.apiKey) {
    init.headers = { 'api-key': options.apiKey };
  }

  const response = await fetch(url.toString(), init);

  if (!response.ok) {
    throw new Error(`Kunne ikke hente data (${response.status})`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const filtered = (payload as PriceSeries[]).filter((item) => item.region === options.region);
  return filtered.length > 0 ? (filtered[0] ?? null) : null;
}

function getCurrentHourIndex(dateIso: string): number {
  const now = new Date();
  const targetDate = new Date(dateIso);

  if (
    now.getFullYear() !== targetDate.getFullYear() ||
    now.getMonth() !== targetDate.getMonth() ||
    now.getDate() !== targetDate.getDate()
  ) {
    return Math.min(now.getHours(), 23);
  }

  return now.getHours();
}

function formatOre(value: number): string {
  return `${Math.round(value)} øre`;
}

function formatUpdated(dateIso: string): string {
  const updated = new Date(dateIso);
  const formatter = new Intl.DateTimeFormat('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `Oppdatert ${formatter.format(updated)}`;
}

function describeExtrema(values: number[]): { min: number; minHour: number; max: number; maxHour: number } {
  return values.reduce(
    (acc, value, index) => {
      if (value < acc.min) {
        acc.min = value;
        acc.minHour = index;
      }

      if (value > acc.max) {
        acc.max = value;
        acc.maxHour = index;
      }

      return acc;
    },
    { min: Number.POSITIVE_INFINITY, minHour: 0, max: Number.NEGATIVE_INFINITY, maxHour: 0 },
  );
}

function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`;
}

function createWidgetSkeleton(container: HTMLElement): WidgetState {
  const root = document.createElement('section');
  root.className = 'strompris-widget';

  const header = document.createElement('header');
  header.className = 'strompris-widget__header';

  const title = document.createElement('h2');
  title.className = 'strompris-widget__title';
  title.textContent = 'Strømprisene på Askøy i dag';

  header.append(title);

  const stats = document.createElement('div');
  stats.className = 'strompris-widget__stats';

  const nowStat = document.createElement('div');
  nowStat.className = 'strompris-widget__stat';
  nowStat.dataset.stat = 'now';
  nowStat.innerHTML = `
    <div class="strompris-widget__label">NÅ</div>
    <div class="strompris-widget__value strompris-widget__value--current">–</div>
    <div class="strompris-widget__unit">øre/kWh</div>
  `;

  const avgStat = document.createElement('div');
  avgStat.className = 'strompris-widget__stat';
  avgStat.dataset.stat = 'avg';
  avgStat.innerHTML = `
    <div class="strompris-widget__label">SNITT</div>
    <div class="strompris-widget__value strompris-widget__value--avg">–</div>
    <div class="strompris-widget__unit">øre/kWh</div>
  `;

  const maxStat = document.createElement('div');
  maxStat.className = 'strompris-widget__stat';
  maxStat.dataset.stat = 'max';
  maxStat.innerHTML = `
    <div class="strompris-widget__label">MAX</div>
    <div class="strompris-widget__value strompris-widget__value--max">–</div>
    <div class="strompris-widget__unit">øre/kWh</div>
  `;

  const minStat = document.createElement('div');
  minStat.className = 'strompris-widget__stat';
  minStat.dataset.stat = 'min';
  minStat.innerHTML = `
    <div class="strompris-widget__label">MIN</div>
    <div class="strompris-widget__value strompris-widget__value--min">–</div>
    <div class="strompris-widget__unit">øre/kWh</div>
  `;

  stats.append(nowStat, avgStat, maxStat, minStat);

  const leftColumn = document.createElement('div');
  leftColumn.className = 'strompris-widget__left';

  const chartWrapper = document.createElement('div');
  chartWrapper.className = 'strompris-widget__chart';
  const canvas = document.createElement('canvas');
  chartWrapper.appendChild(canvas);

  const chartLegend = document.createElement('div');
  chartLegend.className = 'strompris-widget__chart-legend';

  const footer = document.createElement('footer');
  footer.className = 'strompris-widget__footer';
  footer.innerHTML = `Inkl. moms men uten nettleie.&nbsp;<a class="strompris-widget__source" href="https://www.strompriser.no" target="_blank" rel="noopener noreferrer">Levert av strompriser.no</a>`;

  leftColumn.append(stats, chartWrapper, chartLegend, footer);

  const rightColumn = document.createElement('div');
  rightColumn.className = 'strompris-widget__right';

  const hoursList = document.createElement('div');
  hoursList.className = 'strompris-widget__hours-list';
  hoursList.dataset.hours = 'list';

  rightColumn.append(hoursList);

  root.append(header, leftColumn, rightColumn);
  container.replaceChildren(root);

  return {
    root,
    canvas,
    useSupport: true,
    priceMode: 'with',
  };
}

function createSupportDropdown(state: WidgetState, data: PriceSeries, currentCardIndex: number): HTMLElement {
  const dropdown = document.createElement('select');
  dropdown.className = 'strompris-widget__support-dropdown';
  dropdown.innerHTML = `
    <option value="with">Med strømstøtte</option>
    <option value="without">Uten strømstøtte</option>
    <option value="norgespris">Norgespris</option>
  `;
  dropdown.value = state.priceMode;

  dropdown.addEventListener('change', (e) => {
    const target = e.target as HTMLSelectElement;
    state.priceMode = target.value as 'with' | 'without' | 'norgespris';
    state.useSupport = target.value === 'with';

    // Recalculate and re-render only the info card
    let currentPrice: number;

    if (state.priceMode === 'norgespris') {
      // Norgespris is a flat 50 øre with VAT applied
      currentPrice = applyVAT(50);
    } else {
      const series = state.useSupport ? data.dailyPriceArrayWithSupport : data.dailyPriceArray;
      const currentIndex = getCurrentHourIndex(data.date);
      const currentValue = series[currentIndex] ?? series[0] ?? 0;
      currentPrice = applyVAT(currentValue);
    }

    // Update info card calculations only
    const cardDisplay = state.root.querySelector('.strompris-widget__card-display');
    const dotsContainer = state.root.querySelector('.strompris-widget__card-dots');
    if (cardDisplay && dotsContainer) {
      updateCardDisplay(state, cardDisplay as HTMLElement, dotsContainer as HTMLElement, currentCardIndex, currentPrice);
    }
  });

  return dropdown;
}

function updateCardDisplay(
  state: WidgetState,
  display: HTMLElement,
  dots: HTMLElement,
  index: number,
  price: number,
): void {
  const card = INFO_CARDS[index];
  if (!card || !state.currentPriceData) {
    return;
  }

  // Calculate daily average based on price mode
  let averagePrice: number;
  if (state.priceMode === 'norgespris') {
    averagePrice = applyVAT(50);
  } else if (state.priceMode === 'with') {
    averagePrice = applyVAT(state.currentPriceData.dailyPriceAverageWithSupport);
  } else {
    averagePrice = applyVAT(state.currentPriceData.dailyPriceAverage);
  }

  const result = card.calculate({
    priceOrePerKwh: price,
    averageOrePerKwh: averagePrice,
    priceMode: state.priceMode,
  });

  const headlineText = card.id === 'oven' ? 'I dag koster det' : 'Akkurat nå koster det';

  display.innerHTML = `
    <div class="strompris-widget__card">
      <div class="strompris-widget__card-image">
        <img src="${card.icon}" alt="${card.title}" class="strompris-widget__card-icon" />
      </div>
      <div class="strompris-widget__card-content">
        <h3 class="strompris-widget__card-headline">${headlineText}</h3>
        <div class="strompris-widget__card-price">
          <span class="strompris-widget__card-value">${result.value}</span>
          <span class="strompris-widget__card-unit">${result.unit}</span>
        </div>
        <h4 class="strompris-widget__card-title">${card.title}</h4>
        <div class="strompris-widget__card-dropdown-container"></div>
      </div>
    </div>
  `;

  // Insert dropdown into the container
  const dropdownContainer = display.querySelector('.strompris-widget__card-dropdown-container');
  if (dropdownContainer && state.currentPriceData) {
    dropdownContainer.appendChild(createSupportDropdown(state, state.currentPriceData, index));
  }

  dots.innerHTML = INFO_CARDS.map((_, i) =>
    `<button class="strompris-widget__card-dot ${i === index ? 'active' : ''}" 
            data-index="${i}" 
            aria-label="Kort ${i + 1}"></button>`,
  ).join('');

  const cardElement = display.querySelector('.strompris-widget__card') as HTMLElement | null;
  if (cardElement) {
    const measuredHeight = cardElement.scrollHeight;
    if (measuredHeight > (state.cardDisplayHeight ?? 0)) {
      state.cardDisplayHeight = measuredHeight;
    }
  }

  if (state.cardDisplayHeight !== undefined) {
    display.style.height = `${state.cardDisplayHeight}px`;
  }
}

function renderInfoCards(state: WidgetState, currentPrice: number): void {
  const container = state.root.querySelector('[data-hours="list"]');
  if (!container) {
    return;
  }

  container.innerHTML = '';
  container.className = 'strompris-widget__info-cards';

  const cardDisplay = document.createElement('div');
  cardDisplay.className = 'strompris-widget__card-display';

  const dotsContainer = document.createElement('div');
  dotsContainer.className = 'strompris-widget__card-dots';

  delete state.cardDisplayHeight;
  updateCardDisplay(state, cardDisplay, dotsContainer, 0, currentPrice);

  container.append(cardDisplay, dotsContainer);

  if (state.carouselInterval) {
    clearInterval(state.carouselInterval);
  }

  state.currentCardIndex = 0;
  state.carouselInterval = window.setInterval(() => {
    state.currentCardIndex = ((state.currentCardIndex ?? 0) + 1) % INFO_CARDS.length;
    updateCardDisplay(state, cardDisplay, dotsContainer, state.currentCardIndex, currentPrice);
  }, 10000);

  dotsContainer.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('strompris-widget__card-dot')) {
      const newIndex = parseInt(target.dataset.index ?? '0', 10);
      state.currentCardIndex = newIndex;
      updateCardDisplay(state, cardDisplay, dotsContainer, newIndex, currentPrice);

      if (state.carouselInterval) {
        clearInterval(state.carouselInterval);
        state.carouselInterval = window.setInterval(() => {
          state.currentCardIndex = ((state.currentCardIndex ?? 0) + 1) % INFO_CARDS.length;
          updateCardDisplay(state, cardDisplay, dotsContainer, state.currentCardIndex, currentPrice);
        }, 10000);
      }
    }
  });
}

function renderStats(state: WidgetState, data: PriceSeries): void {
  // Always use support prices for stats (dropdown only affects info cards)
  const series = Array.isArray(data.dailyPriceArrayWithSupport) ? data.dailyPriceArrayWithSupport : [];

  if (series.length === 0) {
    throw new Error('Ingen timespriser tilgjengelig.');
  }

  const currentIndex = Math.min(getCurrentHourIndex(data.date), series.length - 1);
  const currentValue = series[currentIndex] ?? series[series.length - 1];
  if (currentValue === undefined) {
    throw new Error('Mangler prisdata for valgt tidspunkt.');
  }

  // Apply VAT to current price for info cards
  const currentPriceWithVAT = applyVAT(currentValue);

  const nowValue = state.root.querySelector('[data-stat="now"] .strompris-widget__value');
  const avgValue = state.root.querySelector('[data-stat="avg"] .strompris-widget__value');
  const maxValue = state.root.querySelector('[data-stat="max"] .strompris-widget__value');
  const minValue = state.root.querySelector('[data-stat="min"] .strompris-widget__value');

  if (nowValue) {
    nowValue.textContent = Math.round(applyVAT(currentValue)).toString();
  }

  if (avgValue) {
    avgValue.textContent = Math.round(applyVAT(data.dailyPriceAverageWithSupport)).toString();
  }

  if (maxValue) {
    maxValue.textContent = Math.round(applyVAT(data.dailyPriceMaxWithSupport)).toString();
  }

  if (minValue) {
    minValue.textContent = Math.round(applyVAT(data.dailyPriceMinWithSupport)).toString();
  }

  renderInfoCards(state, currentPriceWithVAT);
}

function renderChartLegend(state: WidgetState, hasSupport: boolean): void {
  const legendContainer = state.root.querySelector('.strompris-widget__chart-legend');
  if (!legendContainer) {
    return;
  }

  const spotLabel = `
    <div class="strompris-widget__legend-item">
      <span class="strompris-widget__legend-color" style="background-color: #3577c0;"></span>
      <span class="strompris-widget__legend-label">Spotpris</span>
    </div>
  `;

  const norgesprisLabel = `
    <div class="strompris-widget__legend-item">
      <span class="strompris-widget__legend-color" style="background-color: #f59e0b;"></span>
      <span class="strompris-widget__legend-label">Norgespris</span>
    </div>
  `;

  const supportLabel = hasSupport ? `
    <div class="strompris-widget__legend-item">
      <span class="strompris-widget__legend-color" style="background-color: #06d6a0;"></span>
      <span class="strompris-widget__legend-label">Med strømstøtte</span>
    </div>
  ` : '';

  legendContainer.innerHTML = spotLabel + norgesprisLabel + supportLabel;
}

function renderChart(state: WidgetState, data: PriceSeries): void {
  if (state.chart) {
    state.chart.destroy();
  }

  const spotPrices = Array.isArray(data.dailyPriceArray) ? data.dailyPriceArray : [];

  // Apply VAT to all prices
  const spotPricesWithVAT = spotPrices.map(price => applyVAT(price));

  // Calculate support prices: when price WITH VAT > 91.3 øre, show: 91.3 + (price - 91.3) * 0.1
  const supportPricesWithVAT = spotPricesWithVAT.map(priceWithVAT => {
    if (priceWithVAT > 91.3) {
      const excess = priceWithVAT - 91.3;
      const yourShare = excess * 0.1; // You pay 10% of excess
      return 91.3 + yourShare;
    }
    return null;
  });

  const norgesprisData = spotPrices.map(() => 50);

  const hours = spotPrices.map((_, index) => formatHour(index));

  // Check if we should show the support dataset at all
  const hasAnySupport = supportPricesWithVAT.some(price => price !== null);

  // Render custom legend
  renderChartLegend(state, hasAnySupport);

  const datasets: ChartDataset<'line', (number | null)[]>[] = [
    {
      label: 'Spotpris',
      data: spotPricesWithVAT,
      backgroundColor: 'transparent',
      borderColor: '#3577c0',
      fill: false,
      stepped: true,
      pointRadius: 0,
      borderWidth: 2,
    },
    {
      label: 'Norgespris',
      data: norgesprisData,
      backgroundColor: 'transparent',
      borderColor: '#f59e0b',
      fill: false,
      stepped: false,
      pointRadius: 0,
      borderWidth: 2,
      spanGaps: false,
    },
  ];

  if (hasAnySupport) {
    datasets.push({
      label: 'Med strømstøtte',
      data: supportPricesWithVAT,
      backgroundColor: 'transparent',
      borderColor: '#06d6a0',
      fill: false,
      stepped: true,
      pointRadius: 0,
      borderWidth: 2,
      spanGaps: false,
    });
  }

  state.chart = new Chart(state.canvas, {
    type: 'line',
    data: {
      labels: hours,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          display: true,
          ticks: {
            maxTicksLimit: 6,
            color: '#52616b',
          },
          grid: {
            display: false,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          ticks: {
            color: '#52616b',
            callback: (value) => `${value} øre`,
          },
          grid: {
            color: 'rgba(82, 97, 107, 0.1)',
          },
        },
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (context) => {
              const parsed = context.parsed?.y;
              const value = typeof parsed === 'number' ? parsed : 0;
              const label = context.dataset.label || '';
              return `${label}: ${formatOre(value)}`;
            },
          },
        },
        legend: {
          display: false,
        },
      },
    },
  });

}

export async function renderWidget(options: WidgetOptions): Promise<void> {
  ensureStylesInjected();

  const state = createWidgetSkeleton(options.container);
  state.useSupport = true; // DEFAULT: with strømstøtte

  try {
    const data = await fetchPrices(options);

    if (!data || !Array.isArray(data.dailyPriceArrayWithSupport)) {
      throw new Error('Fant ingen priser for i dag.');
    }

    state.currentPriceData = data; // STORE DATA

    renderStats(state, data);
    renderChart(state, data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Strømpris-widget feilet', error);

    if (state.carouselInterval !== undefined) {
      clearInterval(state.carouselInterval);
      delete state.carouselInterval;
    }

    state.root.replaceChildren(createErrorMessage((error as Error).message));
  }
}

function createErrorMessage(message: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'strompris-widget__error';
  wrapper.textContent = message;
  return wrapper;
}

